import {
  ACTION_NETWORK_ERROR_EVENT, ACTION_TIMEOUT_MS, GATEWAY_FAILURE_STATUSES, JSON_CONTENT_TYPE,
  NETWORK_COPY, REQUEST_TIMEOUT_MS, SERVER_UNREACHABLE_EVENT,
} from '../constants/network';

const COPY_BY_CODE = { TIMEOUT: NETWORK_COPY.timeout, OFFLINE: NETWORK_COPY.offline, UNREACHABLE: NETWORK_COPY.unavailable };

/** A gateway page, not our API: our API answers 502/503/504 in JSON with a message to show. */
const isGatewayPage = (response) => GATEWAY_FAILURE_STATUSES.includes(response.status)
  && !(response.headers?.get?.('content-type') || '').includes(JSON_CONTENT_TYPE);

/**
 * fetch with a deadline covering both headers and body consumption.
 * Only transport failures (offline, timeout, fetch rejection, gateway page) become network errors;
 * an error thrown by `consume` is the caller's and passes through untouched.
 * `external`: a third-party API — its failures never mark our server unreachable.
 */
export default async function serverRequest(url, options = {}, consume = (response) => response) {
  const { signal, timeoutMs, interactive, external, ...config } = options;
  const action = interactive ?? !['GET', 'HEAD', 'OPTIONS'].includes((config.method || 'GET').toUpperCase());
  const controller = new AbortController();
  let timer;
  let timedOut = false;
  const cancel = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', cancel, { once: true });

  const failure = (code, status) => {
    const error = new Error(COPY_BY_CODE[code]);
    error.isNetworkError = true;
    error.code = code;
    error.status = status;
    if (typeof window !== 'undefined') {
      if (!external) window.dispatchEvent(new CustomEvent(SERVER_UNREACHABLE_EVENT));
      if (action) window.dispatchEvent(new CustomEvent(ACTION_NETWORK_ERROR_EVENT, { detail: { message: error.message, code } }));
    }
    return error;
  };

  try {
    if (signal?.aborted) { cancel(); throw new DOMException('Aborted', 'AbortError'); }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw failure('OFFLINE');
    const deadline = new Promise((_, reject) => {
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(failure('TIMEOUT'));
      }, timeoutMs ?? (action ? ACTION_TIMEOUT_MS : REQUEST_TIMEOUT_MS));
    });
    const exchange = fetch(url, { ...config, signal: controller.signal }).then((response) => {
      if (isGatewayPage(response)) throw failure('UNREACHABLE', response.status);
      return consume(response);
    }, (error) => {
      // A caller abort stays an AbortError; after our deadline the race is already settled.
      if (timedOut || signal?.aborted) throw error;
      throw failure('UNREACHABLE');
    });
    return await Promise.race([exchange, deadline]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}
